import { ComponentIds, HierarchyUtils, ProductUtils, TransformUtils } from '@inter-ikea-core/core';
import { PlannerValidation, ProductType } from '@inter-ikea-storageone/range';
export var Side;
(function (Side) {
    Side[Side["RIGHT"] = 0] = "RIGHT";
    Side[Side["LEFT"] = 1] = "LEFT";
    Side[Side["CENTER"] = 2] = "CENTER";
    Side[Side["UNDETERMINED"] = 3] = "UNDETERMINED";
})(Side || (Side = {}));
/**
 * Checks if a child is left, right, or center of parent
 * Note that the child must be a direct child of the parent
 */
export const getEntityPlacementSideOfParent = (parent, entity) => {
    // Note that Core does NOT set the parent of the entity being validated, so we must calculate relative position
    // in intended parent space ourselves here
    const entityWorldPosition = TransformUtils.worldPosition(entity);
    const localPosInParent = TransformUtils.worldTransform(parent).inverseTransformPosition(entityWorldPosition);
    if (localPosInParent.X == 0.0) {
        return Side.CENTER;
    }
    else if (localPosInParent.X > 0.0) {
        return Side.RIGHT;
    }
    return Side.LEFT;
};
/**
 * Checks if children are on the same side of a parent
 * Note that the children must be direct children of the parent
 */
export const areChildrenOnSameSideOfParent = (parent, child1, child2) => {
    return (getEntityPlacementSideOfParent(parent, child1) == getEntityPlacementSideOfParent(parent, child2));
};
/**
 * We don't want a door covering half the L-divider compartment.
 */
export class LDividerIn75cmFrameCanNotHave25cmDoorInFrontRule extends PlannerValidation {
    constructor(coreApi) {
        super(coreApi, 'LDividerIn75cmFrameCanNotHave25cmDoorInFrontRule');
        this.checkDoorDividerCombo = (frame, door, divider) => {
            const onSameSide = areChildrenOnSameSideOfParent(frame, divider, door);
            const doorWidth = this.coreApi.entities.getEntityComSize(door)?.width;
            switch (doorWidth) {
                case 500.0:
                    if (onSameSide)
                        return this.validationOK();
                    else
                        return this.validationFail(`A door with width ${doorWidth} must be placed on same side as ${ProductUtils.productRef(divider)} when placed in ${ProductUtils.productRef(frame)}`);
                case 250.0:
                    if (onSameSide)
                        return this.validationFail(`A door with width ${doorWidth} cannot be placed on same side as ${ProductUtils.productRef(divider)} when placed in ${ProductUtils.productRef(frame)}`);
                    else
                        return this.validationOK();
                default:
                    // This rule don't care about any other widths
                    return this.validationOK();
            }
        };
        this.coreApi = coreApi;
    }
    validateCurrentState() {
        return this.validationOK();
    }
    validateSnap(snapActionCandidate) {
        const entity = snapActionCandidate.guestRoot;
        const parent = snapActionCandidate.host;
        if (!parent)
            return this.validationOK();
        if (!this.shouldRuleApply(parent))
            return this.validationOK();
        if (this.coreApi.entities.isProductType(entity, ProductType.PAX_L_DIVIDER))
            return this.canPlaceDividerInFrame(entity, parent);
        if (entity.hasComponent(ComponentIds.DOOR_COMPONENT_ID))
            return this.canPlaceDoorInFrame(entity, parent);
        return this.validationOK();
    }
    shouldRuleApply(parent) {
        // Rule only applies to parents that are frames
        if (!parent.hasComponent(ComponentIds.FRAME_COMPONENT_ID))
            return false;
        // This rule only applies when placing into 750 frames
        const frameWidth = this.coreApi.entities.getEntityComSize(parent)?.width;
        if (frameWidth != 750.0)
            return false;
        return true;
    }
    canPlaceDoorInFrame(door, frame) {
        const divider = HierarchyUtils.children(frame).find((it) => this.coreApi.entities.isProductType(it, ProductType.PAX_L_DIVIDER));
        if (!divider)
            return this.validationOK();
        return this.checkDoorDividerCombo(frame, door, divider);
    }
    canPlaceDividerInFrame(divider, frame) {
        // Here we simply test if each door is ok with being placed with the divider instead of checking from the divider's point of view
        const results = HierarchyUtils.children(frame)
            ?.filter((it) => {
            return it.hasComponent(ComponentIds.DOOR_COMPONENT_ID);
        }) // Filter on each door among direct children
            ?.map((door) => {
            return this.checkDoorDividerCombo(frame, door, divider);
        });
        const failedResult = results.find((result) => {
            return result?.resultType.name === 'FAIL';
        });
        if (failedResult) {
            return failedResult;
        }
        return this.validationOK();
    }
}
//# sourceMappingURL=LDividerIn75cmFrameCanNotHave25cmDoorInFrontRule.js.map